import React, { useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import {
  buildTransitionPayload,
  getAssigneeIdForRole,
  getNextAssigneeRoleForAction,
  getOcActionLabel,
  getOcStatusLabel,
  formatOcCodeLabel,
  OC_ASSIGNEE_ROLE_LABELS,
} from '../../utils/ocRequest';
import {
  canOcTransition,
  formatOcTransitionError,
} from '../../utils/ocRequestRoles';
import { showToast } from '../../config_and_helpers/helpers';
import { logInfo } from '../../config_and_helpers/logger';

const LOG_CONTEXT = 'OcTransitionBar';

/**
 * Secondary actions where the note is an audit record on the workflow action, not
 * a message to the customer. `request_client_clarification` also moves the request
 * to NEEDS_CLIENT_CLARIFICATION, so an internal note belongs in Comments instead.
 */
const AUDIT_ONLY_NOTE_ACTIONS = new Set([
  'request_client_clarification',
  'cancel_request',
  'reject_request',
]);

/**
 * Primary Complete transition control with optional handoff note for the next reviewer.
 * Next-stage email handoff uses Stage assignees on the request (no separate Assign next picker).
 *
 * @param {Object} props
 * @param {Function} [props.beforeTransition] - async (action) => false | true | { abort?: boolean, extraPayload?: Object }
 * @param {Function} [props.getExtraPayload] - (action) => Object merged into transition payload
 */
const OcTransitionBar = ({
  api,
  requestId,
  request = null,
  action,
  label = 'Complete',
  secondaryActions = [],
  onSuccess,
  disabled = false,
  beforeTransition = null,
  getExtraPayload = null,
}) => {
  const [comments, setComments] = useState('');
  const [saving, setSaving] = useState(false);

  const status = request?.status;
  const nextRole = getNextAssigneeRoleForAction(action);
  const stageNextAssigneeId = nextRole
    ? getAssigneeIdForRole(request, nextRole)
    : null;
  const nextAssigneeMissing = Boolean(nextRole) && stageNextAssigneeId == null;

  const runTransition = async (transitionAction, transitionLabel) => {
    if (!api || !transitionAction) return;
    if (!canOcTransition(transitionAction, status)) {
      showToast(
        'This step is not available in the current status',
        'warning',
        'top-right',
        4000
      );
      return;
    }
    setSaving(true);
    let extraPayload = {};
    if (typeof beforeTransition === 'function') {
      const gate = await beforeTransition(transitionAction);
      if (gate === false || gate?.abort) {
        setSaving(false);
        return;
      }
      if (gate?.extraPayload && typeof gate.extraPayload === 'object') {
        extraPayload = { ...extraPayload, ...gate.extraPayload };
      }
    }
    if (typeof getExtraPayload === 'function') {
      const more = getExtraPayload(transitionAction) || {};
      extraPayload = { ...extraPayload, ...more };
    }
    // Do not send next_assignee_id — backend should use stage assignees for handoff.
    const payload = { ...extraPayload };
    const result = await api.transition(
      buildTransitionPayload(transitionAction, {
        comments,
        payload,
      })
    );
    setSaving(false);
    if (result.success) {
      logInfo(LOG_CONTEXT, 'transition success', {
        requestId,
        action: transitionAction,
        nextRole: getNextAssigneeRoleForAction(transitionAction),
        stageNextAssigneeId: getAssigneeIdForRole(
          result.data || request,
          getNextAssigneeRoleForAction(transitionAction)
        ),
        candidateCount: Array.isArray(payload.candidates)
          ? payload.candidates.length
          : undefined,
        mode: api.mode,
      });
      showToast(
        `${transitionLabel || getOcActionLabel(transitionAction)} succeeded`,
        'success',
        'top-right',
        3000
      );
      setComments('');
      onSuccess?.(result.data);
    } else {
      showToast(formatOcTransitionError(result), 'error', 'top-right', 5000);
    }
  };

  const primaryAllowed = !action || canOcTransition(action, status);
  const auditOnlyActions = secondaryActions.filter(item =>
    AUDIT_ONLY_NOTE_ACTIONS.has(item.action)
  );
  const clarificationAction = secondaryActions.find(
    item => item.action === 'request_client_clarification'
  );

  return (
    <div className="oc-transition-bar">
      <Form.Group className="mb-2">
        <Form.Label>Handoff note for the next reviewer (optional)</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={comments}
          onChange={e => setComments(e.target.value)}
        />
        <Form.Text className="text-muted">
          Saved with this step. The next stage will see it under Notes from previous
          steps — not the Comments thread on Detail.
          {auditOnlyActions.length ? (
            <>
              {' '}
              For{' '}
              {auditOnlyActions
                .map(item => item.label || getOcActionLabel(item.action))
                .join(', ')}
              , the note is an audit record only and is never emailed to the customer.
            </>
          ) : null}
        </Form.Text>
      </Form.Group>
      {clarificationAction ? (
        <Alert variant="info" className="py-2 small mb-2">
          <strong>
            {clarificationAction.label ||
              getOcActionLabel('request_client_clarification')}
          </strong>{' '}
          sets the status to{' '}
          {getOcStatusLabel('NEEDS_CLIENT_CLARIFICATION')} and emails the lab. The
          customer is not emailed. To record an internal note without changing status,
          use Comments instead.
        </Alert>
      ) : null}
      {nextAssigneeMissing ? (
        <Alert variant="warning" className="py-2 small mb-2">
          No stage assignee for{' '}
          {OC_ASSIGNEE_ROLE_LABELS[nextRole] || formatOcCodeLabel(nextRole)} — set Stage assignees
          above so the handoff email can be sent.
        </Alert>
      ) : null}
      <div className="d-flex flex-wrap gap-2">
        <Button
          variant="primary"
          className="oc-primary-btn"
          disabled={disabled || saving || !action || !primaryAllowed || !api}
          onClick={() => runTransition(action, label)}
        >
          {saving ? <Spinner size="sm" animation="border" /> : label}
        </Button>
        {secondaryActions.map(item => (
          <Button
            key={item.action}
            variant={item.variant || 'outline-secondary'}
            size="sm"
            disabled={
              saving || disabled || !api || !canOcTransition(item.action, status)
            }
            onClick={() =>
              runTransition(item.action, item.label || getOcActionLabel(item.action))
            }
          >
            {item.label || getOcActionLabel(item.action)}
          </Button>
        ))}
      </div>
    </div>
  );
};

export default OcTransitionBar;
